import React, { useState, useEffect } from 'react';
import {
    Paper,
    TextField,
    Button,
    Grid,
    Typography,
    Card,
    CardContent,
    InputLabel,
    Select,
    MenuItem,
    Box,
    InputAdornment,
    Fade,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import 'dayjs/locale/en-gb';
import { bookingAPI } from '../services/api';
import { toast } from 'react-toastify';
import { COURT_NUMBERS } from '../utils/constants';
import ReCAPTCHA from 'react-google-recaptcha';
import { datePickerStyles, datePickerPopperStyles } from '../styles/datePickerStyles';

// Logo color palette
const logoColors = {
    primaryBlue: '#3f51b5',
    brightYellow: '#c4d835',
    darkBlue: '#2c387e',
    lightYellow: '#d4e157',
};

// Set global locale to use DD/MM/YYYY format
dayjs.locale('en-gb');

const BookingForm = ({ onBookingCreated, isAdmin = false }) => {

    // Check if user is admin (only from prop, not from localStorage)
    const isAdminUser = isAdmin;

    const [formData, setFormData] = useState({
        customerName: '',
        customerEmail: '',
        customerPhone: '',
        bookingDate: dayjs(),
        startTime: '',
        endTime: '',
        courtNumber: 1,
        totalAmount: 0,
        opponentName: '',
        remarks: ''
    });

    const [confirmOpen, setConfirmOpen] = useState(false);
    const [captchaValue, setCaptchaValue] = useState(null);
    const [validationErrors, setValidationErrors] = useState({});

    const [availableSlots, setAvailableSlots] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedSlot, setSelectedSlot] = useState(null);

    // Pakistani phone number validation
    const validatePakistaniPhone = (phone) => {
        // Remove all spaces and special characters except +
        const cleanPhone = phone.replace(/[\s\-\(\)]/g, '');
        
        // Check if it starts with +92
        if (cleanPhone.startsWith('+92')) {
            // Should be +92 followed by 10 digits
            const phoneRegex = /^\+92[0-9]{10}$/;
            return phoneRegex.test(cleanPhone);
        }
        
        // Check if it starts with 0 (local format)
        if (cleanPhone.startsWith('0')) {
            // Should be 11 digits starting with 0
            const phoneRegex = /^0[0-9]{10}$/;
            return phoneRegex.test(cleanPhone);
        }
        
        return false;
    };

    // Format phone number to +92 format
    const formatPhoneNumber = (phone) => {
        const cleanPhone = phone.replace(/[\s\-\(\)]/g, '');
        
        if (cleanPhone.startsWith('0') && cleanPhone.length === 11) {
            // Convert 03xx to +923xx
            return `+92${cleanPhone.substring(1)}`;
        }
        
        if (cleanPhone.startsWith('3') && cleanPhone.length === 10) {
            // Convert 3xx to +923xx
            return `+92${cleanPhone}`;
        }
        
        if (cleanPhone.startsWith('+92')) {
            return cleanPhone;
        }
        
        return phone;
    };

    // Validate form fields
    const validateForm = () => {
        const errors = {};

        // Name validation
        if (!formData.customerName.trim()) {
            errors.customerName = 'Name is required';
        } else if (formData.customerName.trim().length < 2) {
            errors.customerName = 'Name must be at least 2 characters';
        }

        // Email validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!formData.customerEmail.trim()) {
            errors.customerEmail = 'Email is required';
        } else if (!emailRegex.test(formData.customerEmail)) {
            errors.customerEmail = 'Please enter a valid email address';
        }

        // Phone validation
        if (!formData.customerPhone.trim()) {
            errors.customerPhone = 'Phone number is required';
        } else if (!validatePakistaniPhone(formData.customerPhone)) {
            errors.customerPhone = 'Please enter a valid Pakistani phone number (e.g., +92 300 1234567 or 0300 1234567)';
        }

        setValidationErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handlePhoneChange = (e) => {
        let value = e.target.value;

        // Remove any non-digit characters except +
        value = value.replace(/[^\d+]/g, '');
        
        // Enforce maximum length
        if (value.startsWith('+92')) {
            value = value.slice(0, 13); // +92 plus 10 digits
        } else if (value.startsWith('0')) {
            value = value.slice(0, 11); // 0 plus 10 digits
        }
        
        // Auto-format phone number
        if (value.startsWith('03') && !value.startsWith('+92')) {
            value = `+92 ${value.substring(1)}`;
        }

         // Auto-format phone number
        if (value.startsWith('3') && !value.startsWith('+92')) {
            value = `+92 ${value.substring(0)}`;
        }
        
        setFormData({ ...formData, customerPhone: value });
        
        // Clear validation error when user starts typing
        if (validationErrors.customerPhone) {
            setValidationErrors({ ...validationErrors, customerPhone: '' });
        }
    };

    const handleInputChange = (field) => (e) => {
    const value = field === 'totalAmount' ? parseFloat(e.target.value) || 0 : e.target.value;
    
    setFormData({ ...formData, [field]: value });
                 
    // Clear validation error when user starts typing
    if (validationErrors[field]) {
        setValidationErrors({ ...validationErrors, [field]: '' });
    }
};

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // Validate form
        if (!validateForm()) {
            toast.error('Please fix the validation errors');
            return;
        }
        
        if (!captchaValue) {
            toast.error('Please complete the captcha!');
            return;
        }

        setConfirmOpen(true);
    };

    const handleConfirmYes = async () => {
        setConfirmOpen(false);
        setLoading(true);
        try {
            const bookingData = {
                ...formData,
                bookingDate: formData.bookingDate.format('YYYY-MM-DD'),
                startTime: selectedSlot?.startTime,
                endTime: selectedSlot?.endTime,
                totalAmount: formData.totalAmount,
                customerPhone: formatPhoneNumber(formData.customerPhone), // Ensure proper format
            };

            await bookingAPI.createBooking(bookingData);
            toast.success('Booking created successfully!');
            onBookingCreated && onBookingCreated();

            setFormData({
                customerName: '',
                customerEmail: '',
                customerPhone: '',
                bookingDate: dayjs(),
                startTime: '',
                endTime: '',
                courtNumber: 1,
                totalAmount: 0,
                opponentName: '',
                remarks: ''
            });
            setSelectedSlot(null);
            setValidationErrors({});
        } catch (error) {
            if (error.response?.status === 409) {
                toast.error('Time slot already booked!');
            } else {
                toast.error('Failed to create booking: ' + (error.response?.data?.message || error.message));
            }
        } finally {
            setLoading(false);
        }
    };

    const fetchAvailableSlots = async () => {
        try {
            const response = await bookingAPI.getAvailableSlots(
                formData.bookingDate.format('YYYY-MM-DD'),
                1
            );
            // Ensure response.data is always an array to prevent .map errors
            const slots = Array.isArray(response.data) ? response.data : [];
            setAvailableSlots(slots);
        } catch (error) {
            console.error('Failed to fetch available slots:', error);
            setAvailableSlots([]);
        }
    };

    useEffect(() => {
        if (formData.bookingDate) {
            setSelectedSlot(null); // Reset slot selection on date/court change
            fetchAvailableSlots();
        }
        // eslint-disable-next-line
    }, [formData.bookingDate, formData.courtNumber]);

    // Update form data when slot is selected (for all users)
    useEffect(() => {
        if (selectedSlot) {
            setFormData(prev => ({
                ...prev,
                totalAmount: selectedSlot.price || 0
            }));
        }
    }, [selectedSlot]);

    const renderTimeSlot = (slot, index) => {
        if (!slot || typeof slot !== 'object') return null;
        const displayName = slot.displayName || `${slot.startTime || ''} - ${slot.endTime || ''}`;
        const price = slot.price || 0;
        const isAvailable = slot.isAvailable !== false;
        const hasPendingBooking = slot.hasPendingBooking || false;
        const isSelected = selectedSlot && selectedSlot.startTime === slot.startTime && selectedSlot.endTime === slot.endTime;
        
        // Check if slot is in the past (only for today's date)
        const now = new Date();
        // Use local date instead of UTC to avoid timezone issues
        const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
        const selectedDateStr = formData.bookingDate ? formData.bookingDate.format('YYYY-MM-DD') : today;
        const isPastDay = formData.bookingDate && formData.bookingDate.startOf('day').isBefore(dayjs().startOf('day'));

        const isPastSlot = selectedDateStr === today && slot.endTime && slot.startTime && (() => {
            const [startHours, startMinutes] = String(slot.startTime).split(':').map(Number);
            const [endHours, endMinutes] = String(slot.endTime).split(':').map(Number);

            const slotStartTime = new Date();
            slotStartTime.setHours(startHours, startMinutes, 0, 0);

            const slotEndTime = new Date();
            slotEndTime.setHours(endHours, endMinutes, 0, 0);

            // If the slot wraps past midnight (e.g., 23:00-00:00), treat end as next day
            if (slotEndTime <= slotStartTime) {
                slotEndTime.setDate(slotEndTime.getDate() + 1);
            }

            return now >= slotEndTime;
        })();
        
        const isSelectable = isAvailable && !isPastSlot && !isPastDay;

        // Determine status label for past days only (no labels for future)
        let statusLabel = null;
        if (isPastDay) {
            statusLabel = !isAvailable ? 'Completed' : 'Time Passed';
        }

        return (
            <Box
                key={`slot-${index}`}
                onClick={() => isSelectable && setSelectedSlot(slot)}
                sx={{
                    p: 2,
                    borderRadius: '12px',
                    border: `2px solid ${
                        isSelected 
                            ? logoColors.brightYellow 
                            : (isPastSlot || !isAvailable)
                            ? `${logoColors.darkBlue}80` // Same greyout border for past and unavailable slots
                            : hasPendingBooking
                            ? '#f44336' // Red border for pending bookings (Material-UI error color)
                            : logoColors.lightYellow 
                    }`,
                    backgroundColor: isSelected 
                        ? logoColors.brightYellow
                        : (isPastSlot || !isAvailable)
                        ? `${logoColors.darkBlue}20` // Same greyout background for past and unavailable slots
                        : `${logoColors.lightYellow}10`,
                    color: isSelected 
                        ? logoColors.darkBlue
                        : isPastSlot
                        ? `${logoColors.brightYellow}70` // Slightly brighter text for past slots
                        : !isAvailable
                        ? `${logoColors.brightYellow}60` // Dimmer text for unavailable slots
                        : logoColors.brightYellow,
                    cursor: isSelectable ? 'pointer' : 'not-allowed',
                    opacity: isPastSlot ? 0.7 : (isSelectable ? 1 : 0.6), // Past slots are slightly more visible
                    textAlign: 'center',
                    transition: 'all 0.3s ease',
                    fontWeight: isSelected ? 700 : 500,
                    fontSize: '0.85rem',
                    minHeight: '60px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    boxShadow: isSelected 
                        ? `0 4px 15px ${logoColors.brightYellow}40` 
                        : '0 2px 8px rgba(0,0,0,0.1)',
                    '&:hover': (isSelectable && !isPastSlot) ? {
                        transform: 'translateY(-2px)',
                        boxShadow: hasPendingBooking 
                            ? `0 6px 20px rgba(244,67,54,0.4)` // Red shadow for pending
                            : `0 6px 20px ${logoColors.lightYellow}30`,
                        backgroundColor: isSelected 
                            ? logoColors.brightYellow
                            : `${logoColors.brightYellow}15`,
                        borderColor: logoColors.brightYellow
                    } : {},
                    // Add tooltip for pending bookings
                    position: 'relative',
                    '&::after': isPastSlot ? {
                        content: '"⏰ This time slot has already passed and cannot be booked."',
                        position: 'absolute',
                        bottom: '100%',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        background: `linear-gradient(135deg, ${logoColors.darkBlue} 0%, ${logoColors.primaryBlue} 100%)`,
                        color: `${logoColors.brightYellow}80`,
                        padding: '12px 16px',
                        borderRadius: '12px',
                        fontSize: '13px',
                        fontWeight: 600,
                        zIndex: 1000,
                        opacity: 0,
                        visibility: 'hidden',
                        transition: 'all 0.3s ease',
                        maxWidth: '280px',
                        whiteSpace: 'normal',
                        textAlign: 'center',
                        lineHeight: 1.5,
                        marginBottom: '8px',
                        boxShadow: `0 8px 25px rgba(44, 56, 126, 0.4), 0 0 0 2px ${logoColors.brightYellow}40`,
                        border: `1px solid ${logoColors.brightYellow}60`,
                        backdropFilter: 'blur(10px)',
                        '&::before': {
                            content: '""',
                            position: 'absolute',
                            top: '100%',
                            left: '50%',
                            transform: 'translateX(-50%)',
                            border: '8px solid transparent',
                            borderTopColor: logoColors.darkBlue,
                            filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))'
                        }
                    } : hasPendingBooking ? {
                        content: '"⚠️ Someone has booked this slot but not made payment. You can still book this slot - it will go to whoever pays first!"',
                        position: 'absolute',
                        bottom: '100%',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        background: `linear-gradient(135deg, ${logoColors.darkBlue} 0%, ${logoColors.primaryBlue} 100%)`,
                        color: logoColors.brightYellow,
                        padding: '12px 16px',
                        borderRadius: '12px',
                        fontSize: '13px',
                        fontWeight: 600,
                        zIndex: 1000,
                        opacity: 0,
                        visibility: 'hidden',
                        transition: 'all 0.3s ease',
                        maxWidth: '320px',
                        whiteSpace: 'normal',
                        textAlign: 'center',
                        lineHeight: 1.5,
                        marginBottom: '8px',
                        boxShadow: `0 8px 25px rgba(44, 56, 126, 0.4), 0 0 0 2px ${logoColors.brightYellow}40`,
                        border: `1px solid ${logoColors.brightYellow}60`,
                        backdropFilter: 'blur(10px)',
                        '&::before': {
                            content: '""',
                            position: 'absolute',
                            top: '100%',
                            left: '50%',
                            transform: 'translateX(-50%)',
                            border: '8px solid transparent',
                            borderTopColor: logoColors.darkBlue,
                            filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))'
                        }
                    } : {},
                    '&:hover::after': (isPastSlot || hasPendingBooking) ? {
                        opacity: 1,
                        visibility: 'visible'
                    } : {}
                }}
            >
                <Box sx={{ fontWeight: 600, mb: 0.5 }}>
                    {(isPastDay || isPastSlot) && <span style={{ marginRight: '4px' }}>⏰</span>}
                    {displayName}
                </Box>
                <Box sx={{ fontSize: '0.8rem', opacity: 0.9 }}>
                    {isPastDay ? statusLabel : (isPastSlot ? 'Time Passed' : `Rs ${price}`)}
                </Box>
            </Box>
        );
    };

    return (
        <Paper
            elevation={6}
            sx={{
                maxWidth: 1500,
                width: '100%',
                borderRadius: '20px',
                p: { xs: 3, sm: 5 },
                background: `linear-gradient(135deg, ${logoColors.primaryBlue} 0%, ${logoColors.darkBlue} 50%, ${logoColors.primaryBlue} 100%)`,
                backdropFilter: 'blur(10px)',
                border: `2px solid ${logoColors.lightYellow}40`,
                boxShadow: `0 20px 60px rgba(63, 81, 181, 0.15)`,
                position: 'relative',
            }}
        >
            <Typography 
                variant="h4" 
                gutterBottom 
                sx={{ 
                    fontWeight: 800, 
                    textAlign: 'center', 
                    mb: 4,
                    color: logoColors.brightYellow,
                    textShadow: '2px 2px 4px rgba(0, 0, 0, 0.3)',
                    fontSize: { xs: '1.75rem', md: '2.5rem' }
                }}
            >
                Book Your Futsal Court
            </Typography>

            {/* Step 1: Date Selection */}
                        <Box sx={{ mb: 4 }}>
                            <Typography 
                                variant="h6" 
                                gutterBottom 
                                sx={{ 
                                    mb: 2, 
                                    color: logoColors.brightYellow,
                                    fontWeight: 600,
                                    fontSize: '1.2rem'
                                }}
                            >
                                1. Select a Date
                            </Typography>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                                    <DatePicker
                                        label="Booking Date"
                                        value={formData.bookingDate}
                                        onChange={(newValue) => setFormData({ ...formData, bookingDate: newValue })}
                                        disablePast={!isAdminUser}
                                        format="DD/MM/YYYY"
                                        slotProps={{
                                            ...datePickerPopperStyles,
                                            textField: {
                                                InputProps: {
                                                    sx: {
                                                        color: '#c4d835',
                                                        '& input': {
                                                            color: '#c4d835 !important',
                                                            WebkitTextFillColor: '#c4d835 !important',
                                                            fontWeight: '700 !important'
                                                        }
                                                    }
                                                },
                                                sx: datePickerStyles
                                            }
                                        }}
                                    />
                                </LocalizationProvider>
                                </Grid>
                            </Grid>
                        </Box>

                        {/* Step 2: Time Slot Selection */}
            <Box sx={{ mb: 4 }}>
                <Typography 
                    variant="h6" 
                    gutterBottom 
                    sx={{ 
                        color: logoColors.brightYellow,
                        fontWeight: 600,
                        mb: 3,
                        fontSize: '1.2rem'
                    }}
                >
                    2. Choose an Available Time Slot
                </Typography>
                <Box 
                    sx={{ 
                        display: 'grid', 
                        gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)', md: 'repeat(4, 1fr)' },
                        gap: 2,
                        p: 3,
                        borderRadius: '16px',
                        border: `1px solid ${logoColors.lightYellow}40`,
                        background: 'rgba(255, 255, 255, 0.03)',
                        backdropFilter: 'blur(10px)',
                    }}
                >
                    {availableSlots.length > 0 ? (
                        availableSlots.map((slot, index) => renderTimeSlot(slot, index))
                    ) : (
                        <Typography 
                            variant="body1" 
                            sx={{ 
                                color: logoColors.brightYellow,
                                fontStyle: 'italic',
                                textAlign: 'center',
                                gridColumn: '1 / -1',
                                py: 3,
                                opacity: 0.8
                            }}
                        >
                            No slots available for this date/court.
                        </Typography>
                    )}
                </Box>
            </Box>

            {/* Step 3: Booking Details Form */}
            <Fade in={!!selectedSlot}>
                <Box 
                    component="form" 
                    onSubmit={handleSubmit} 
                    sx={{ 
                        display: selectedSlot ? 'block' : 'none',
                    }}
                >
                    <Typography 
                        variant="h6" 
                        gutterBottom 
                        sx={{ 
                            mb: 3,
                            color: logoColors.brightYellow,
                            fontWeight: 600,
                            fontSize: '1.2rem'
                        }}
                    >
                        3. Enter Your Details
                    </Typography>
                    <Box 
                        sx={{
                            p: 3,
                            borderRadius: '16px',
                            border: `1px solid ${logoColors.lightYellow}40`,
                            background: 'rgba(255, 255, 255, 0.03)',
                            backdropFilter: 'blur(10px)',
                        }}
                    >
                        <Grid container spacing={3}>
                            <Grid item xs={12} sm={2}>
                                <TextField
                                    fullWidth
                                    label="Customer Name"
                                    value={formData.customerName}
                                    onChange={handleInputChange('customerName')}
                                    error={!!validationErrors.customerName}
                                    helperText={validationErrors.customerName}
                                    required
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            backgroundColor: `${logoColors.lightYellow}08`,
                                            borderRadius: '12px',
                                            color: logoColors.brightYellow,
                                            fontWeight: 500,
                                            '& fieldset': {
                                                borderColor: `${logoColors.lightYellow}60`,
                                                borderWidth: '1px'
                                            },
                                            '&:hover fieldset': {
                                                borderColor: logoColors.lightYellow,
                                            },
                                            '&.Mui-focused fieldset': {
                                                borderColor: logoColors.brightYellow,
                                                borderWidth: '2px'
                                            },
                                            '&.Mui-error fieldset': {
                                                borderColor: '#ff4444',
                                            },
                                            '& input': {
                                                color: logoColors.brightYellow,
                                                fontWeight: 500
                                            }
                                        },
                                        '& .MuiInputLabel-root': {
                                            color: `${logoColors.brightYellow}90`,
                                            fontWeight: 500,
                                            '&.Mui-focused': {
                                                color: logoColors.brightYellow,
                                            },
                                            '&.MuiInputLabel-shrink': {
                                                color: logoColors.brightYellow,
                                            },
                                            '&.Mui-error': {
                                                color: '#ff4444',
                                            }
                                        },
                                        '& .MuiFormHelperText-root': {
                                            color: '#ff4444',
                                            fontWeight: 500
                                        }
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={8}>
                                <TextField
                                    fullWidth
                                    label="Email"
                                    type="email"
                                    value={formData.customerEmail}
                                    onChange={handleInputChange('customerEmail')}
                                    error={!!validationErrors.customerEmail}
                                    helperText={validationErrors.customerEmail}
                                    required
                                    sx={{
                                        width: '100%',
                                        minWidth: '350px',
                                        flex: '1 1 60%',
                                        '& .MuiOutlinedInput-root': {
                                            backgroundColor: `${logoColors.lightYellow}08`,
                                            borderRadius: '12px',
                                            color: logoColors.brightYellow,
                                            fontWeight: 500,
                                            '& fieldset': {
                                                borderColor: `${logoColors.lightYellow}60`,
                                                borderWidth: '1px'
                                            },
                                            '&:hover fieldset': {
                                                borderColor: logoColors.lightYellow,
                                            },
                                            '&.Mui-focused fieldset': {
                                                borderColor: logoColors.brightYellow,
                                                borderWidth: '2px'
                                            },
                                            '&.Mui-error fieldset': {
                                                borderColor: '#ff4444',
                                            },
                                            '& input': {
                                                color: logoColors.brightYellow,
                                                fontWeight: 500
                                            }
                                        },
                                        '& .MuiInputLabel-root': {
                                            color: `${logoColors.brightYellow}90`,
                                            fontWeight: 500,
                                            '&.Mui-focused': {
                                                color: logoColors.brightYellow,
                                            },
                                            '&.MuiInputLabel-shrink': {
                                                color: logoColors.brightYellow,
                                            },
                                            '&.Mui-error': {
                                                color: '#ff4444',
                                            }
                                        },
                                        '& .MuiFormHelperText-root': {
                                            color: '#ff4444',
                                            fontWeight: 500
                                        }
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={2}>
                                <TextField
                                    fullWidth
                                    label="Phone Number +92XXXXXXXXXX"
                                    value={formData.customerPhone}
                                    onChange={handlePhoneChange}
                                    error={!!validationErrors.customerPhone}
                                    helperText={validationErrors.customerPhone || 'Enter mobile number (e.g., +92 300 1234567)'}
                                    placeholder="+92 300 1234567"
                                    inputProps={{
                                        maxLength: 13, // For format: +92XXXXXXXXXX
                                        minLength: 11, // For format: 03XXXXXXXXX
                                    }}
                                    required
                                    sx={{
                                        width: '320px',
                                        minWidth: '320px',
                                        maxWidth: '320px',
                                        '& .MuiOutlinedInput-root': {
                                            backgroundColor: `${logoColors.lightYellow}08`,
                                            borderRadius: '12px',
                                            color: logoColors.brightYellow,
                                            fontWeight: 500,
                                            '& fieldset': {
                                                borderColor: `${logoColors.lightYellow}60`,
                                                borderWidth: '1px'
                                            },
                                            '&:hover fieldset': {
                                                borderColor: logoColors.lightYellow,
                                            },
                                            '&.Mui-focused fieldset': {
                                                borderColor: logoColors.brightYellow,
                                                borderWidth: '2px'
                                            },
                                            '&.Mui-error fieldset': {
                                                borderColor: '#ff4444',
                                            },
                                            '& input': {
                                                color: logoColors.brightYellow,
                                                fontWeight: 500
                                            }
                                        },
                                        '& .MuiInputLabel-root': {
                                            color: `${logoColors.brightYellow}90`,
                                            fontWeight: 500,
                                            '&.Mui-focused': {
                                                color: logoColors.brightYellow,
                                            },
                                            '&.MuiInputLabel-shrink': {
                                                color: logoColors.brightYellow,
                                            },
                                            '&.Mui-error': {
                                                color: '#ff4444',
                                            }
                                        },
                                        '& .MuiFormHelperText-root': {
                                            color: validationErrors.customerPhone ? '#ff4444' : `${logoColors.brightYellow}80`,
                                            fontWeight: 500,
                                            fontSize: '0.75rem'
                                        }
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} sx={{ mt: 1 }}>
                                <TextField
                                    fullWidth
                                    label="Total Amount (PKR)"
                                    type="number"
                                    value={formData.totalAmount || ''}
                                    onChange={isAdminUser ? handleInputChange('totalAmount') : undefined}
                                    placeholder={isAdminUser ? "Enter amount" : "Select a time slot"}
                                    InputProps={{ 
                                        readOnly: !isAdminUser,
                                        // Remove spinner arrows
                                        inputProps: {
                                            style: {
                                                // Remove spinner arrows
                                                '&::-webkit-outer-spin-button': {
                                                    '-webkit-appearance': 'none',
                                                    margin: 0,
                                                },
                                                '&::-webkit-inner-spin-button': {
                                                    '-webkit-appearance': 'none',
                                                    margin: 0,
                                                },
                                                // Firefox
                                                '-moz-appearance': 'textfield',
                                            }
                                        }
                                    }}
                                    required
                                    helperText={isAdminUser ? "You can edit the amount as admin" : ""}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            backgroundColor: `${logoColors.brightYellow}15`,
                                            borderRadius: '12px',
                                            '& fieldset': {
                                                borderColor: logoColors.brightYellow,
                                                borderWidth: '2px'
                                            },
                                            '& input': {
                                                color: logoColors.lightYellow,
                                                fontWeight: 700,
                                                fontSize: '1.1rem',
                                                // Remove spinner arrows
                                                '&::-webkit-outer-spin-button': {
                                                    '-webkit-appearance': 'none',
                                                    margin: 0,
                                                },
                                                '&::-webkit-inner-spin-button': {
                                                    '-webkit-appearance': 'none',
                                                    margin: 0,
                                                },
                                                // Firefox
                                                '-moz-appearance': 'textfield',
                                            }
                                        },
                                        '& .MuiInputLabel-root': {
                                            color: logoColors.brightYellow,
                                            fontWeight: 600,
                                            '&.MuiInputLabel-shrink': {
                                                color: logoColors.brightYellow,
                                            }
                                        },
                                        '& .MuiFormHelperText-root': {
                                            color: `${logoColors.brightYellow}80`,
                                            fontWeight: 500,
                                            fontSize: '0.75rem'
                                        }
                                    }}
                                />
                            </Grid>
                            {isAdminUser && (
                                <Grid item xs={12} sm={6} sx={{ mt: 1 }}>
                                    <TextField
                                        fullWidth
                                        label="Opponent Name (optional)"
                                        value={formData.opponentName || ''}
                                        onChange={handleInputChange('opponentName')}
                                        placeholder="Who are they playing against?"
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                backgroundColor: `${logoColors.lightYellow}08`,
                                                borderRadius: '12px',
                                                color: logoColors.brightYellow,
                                                '& fieldset': {
                                                    borderColor: `${logoColors.lightYellow}60`,
                                                },
                                                '& input': {
                                                    color: logoColors.brightYellow,
                                                }
                                            },
                                            '& .MuiInputLabel-root': {
                                                color: `${logoColors.brightYellow}90`,
                                                '&.MuiInputLabel-shrink': {
                                                    color: logoColors.brightYellow,
                                                }
                                            }
                                        }}
                                    />
                                </Grid>
                            )}
                            {isAdminUser && (
                                <Grid item xs={12} sm={6} sx={{ mt: 1 }}>
                                    <TextField
                                        fullWidth
                                        label="Remarks (optional)"
                                        value={formData.remarks || ''}
                                        onChange={handleInputChange('remarks')}
                                        placeholder="e.g. 1500 due, 420 drinks paid online"
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                backgroundColor: `${logoColors.lightYellow}08`,
                                                borderRadius: '12px',
                                                color: logoColors.brightYellow,
                                                '& fieldset': {
                                                    borderColor: `${logoColors.lightYellow}60`,
                                                },
                                                '& input': {
                                                    color: logoColors.brightYellow,
                                                }
                                            },
                                            '& .MuiInputLabel-root': {
                                                color: `${logoColors.brightYellow}90`,
                                                '&.MuiInputLabel-shrink': {
                                                    color: logoColors.brightYellow,
                                                }
                                            }
                                        }}
                                    />
                                </Grid>
                            )}
                            <Grid item xs={12}>
                                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                                    <Box 
                                        sx={{ 
                                            p: 2, 
                                            borderRadius: '12px',
                                            border: `1px solid ${logoColors.lightYellow}40`,
                                            backgroundColor: `${logoColors.lightYellow}05`
                                        }}
                                    >
                                        <ReCAPTCHA
                                            sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
                                            onChange={(value) => setCaptchaValue(value)}
                                        />
                                    </Box>
                                </Box>
                            </Grid>
                            <Grid item xs={12}>
                                <Button
                                    type="submit"
                                    variant="contained"
                                    size="large"
                                    fullWidth
                                    disabled={loading}
                                    sx={{ 
                                        mt: 2, 
                                        py: 2, 
                                        fontWeight: 700, 
                                        fontSize: '1.2rem',
                                        borderRadius: '16px',
                                        background: `linear-gradient(45deg, ${logoColors.brightYellow} 0%, ${logoColors.lightYellow} 100%)`,
                                        color: logoColors.darkBlue,
                                        textTransform: 'none',
                                        boxShadow: `0 8px 24px ${logoColors.brightYellow}40`,
                                        '&:hover': {
                                            background: `linear-gradient(45deg, ${logoColors.lightYellow} 0%, ${logoColors.brightYellow} 100%)`,
                                            transform: 'translateY(-2px)',
                                            boxShadow: `0 12px 32px ${logoColors.brightYellow}50`,
                                        },
                                        '&:disabled': {
                                            background: `${logoColors.lightYellow}50`,
                                            color: `${logoColors.darkBlue}70`
                                        }
                                    }}
                                >
                                    {loading ? 'Booking...' : 'Book Court'}
                                </Button>
                            </Grid>
                        </Grid>
                    </Box>
                </Box>
            </Fade>

            {/* Confirmation Dialog */}
            <Dialog 
                open={confirmOpen} 
                onClose={() => setConfirmOpen(false)}
                PaperProps={{
                    sx: {
                        borderRadius: '16px',
                        border: `2px solid ${logoColors.lightYellow}`,
                        background: `linear-gradient(135deg, ${logoColors.primaryBlue} 0%, ${logoColors.darkBlue} 100%)`
                    }
                }}
            >
                <DialogTitle 
                    sx={{ 
                        color: logoColors.brightYellow,
                        fontWeight: 700,
                        fontSize: '1.5rem',
                        borderBottom: `2px solid ${logoColors.lightYellow}30`,
                        mb: 2
                    }}
                >
                    Order Confirmation
                </DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ color: logoColors.brightYellow, lineHeight: 1.8 }}>
                        You need to pay <strong style={{ color: logoColors.lightYellow }}>half of the fees</strong> for booking confirmation on Account number <strong style={{ color: logoColors.lightYellow }}>PK44BAHL5037008100157501</strong> Bank Name <strong style={{ color: logoColors.lightYellow }}>Bank Al Habib</strong> and send screenshot to the whatsapp number <strong style={{ color: logoColors.lightYellow }}>0323 8148153</strong>. Do you want to proceed?
                        <br /><br />
                        <strong style={{ color: logoColors.lightYellow }}>Note:</strong> An Email will be sent to your email address with the booking details. Please check your spam/junk folder if you do not see it in your inbox.
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ p: 3, gap: 2 }}>
                    <Button 
                        onClick={() => setConfirmOpen(false)} 
                        variant="outlined"
                        sx={{
                            borderColor: logoColors.lightYellow,
                            color: logoColors.lightYellow,
                            borderRadius: '12px',
                            px: 3,
                            fontWeight: 600,
                            '&:hover': {
                                borderColor: logoColors.brightYellow,
                                backgroundColor: `${logoColors.lightYellow}20`,
                                color: logoColors.brightYellow
                            }
                        }}
                    >
                        No
                    </Button>
                    <Button 
                        onClick={handleConfirmYes} 
                        variant="contained"
                        autoFocus
                        sx={{
                            background: `linear-gradient(45deg, ${logoColors.brightYellow} 0%, ${logoColors.lightYellow} 100%)`,
                            color: logoColors.darkBlue,
                            borderRadius: '12px',
                            px: 3,
                            fontWeight: 600,
                            '&:hover': {
                                background: `linear-gradient(45deg, ${logoColors.lightYellow} 0%, ${logoColors.brightYellow} 100%)`,
                            }
                        }}
                    >
                        Yes
                    </Button>
                </DialogActions>
            </Dialog>
        </Paper>
    );
};

export default BookingForm;